import { useEffect, useState } from "react";
import { AnnotationMode, getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { request } from "@/lib/api";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

async function renderPdfPages(data: ArrayBuffer) {
  const pdf = await getDocument({ data }).promise;
  const pages: string[] = [];
  try {
    for (let number = 1; number <= pdf.numPages; number += 1) {
      const page = await pdf.getPage(number);
      const viewport = page.getViewport({ scale: 1.6 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not draw the syllabus");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: context, viewport, annotationMode: AnnotationMode.DISABLE }).promise;
      pages.push(canvas.toDataURL("image/png"));
      page.cleanup();
    }
  } finally {
    await pdf.destroy();
  }
  return pages;
}

function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function SyllabusPreview({ courseId, name }: { courseId: string; name: string }) {
  const [pages, setPages] = useState<string[] | null>(null);
  const [file, setFile] = useState<Blob | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    setFile(null);
    setError("");
    (async () => {
      const response = await request(`/api/student/courses/${encodeURIComponent(courseId)}/syllabus`);
      if (!response.ok) {
        throw new Error("Could not open the syllabus");
      }
      const bytes = await response.arrayBuffer();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const drawn = await renderPdfPages(bytes.slice(0));
      if (cancelled) return;
      setFile(blob);
      setPages(drawn);
    })().catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Could not open the syllabus");
      setPages([]);
    });
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return (
    <section className="student-syllabus" aria-label="Syllabus">
      <div className="student-syllabus__bar">
        <span className="student-syllabus__name">{name}</span>
        <button
          type="button"
          className="student-text-link student-syllabus__download"
          disabled={!file}
          onClick={() => {
            if (file) saveFile(file, name);
          }}
        >
          Download
        </button>
      </div>
      <div className="student-syllabus__pages">
        {error ? <p className="student-syllabus__status">{error}</p> : null}
        {!error && !pages ? <p className="student-syllabus__status">Loading syllabus…</p> : null}
        {pages?.map((src, index) => (
          <img
            key={index}
            className="student-syllabus__page"
            src={src}
            alt={`${name}, page ${index + 1}`}
            draggable={false}
          />
        ))}
      </div>
    </section>
  );
}
